import { Button, styled } from '@mui/material';

// Gold color palette - premium/CTA specific
const GOLD_COLORS = {
  light: '#E2B034', // Top of gradient
  dark: '#B3861F', // Bottom of gradient
  hover: {
    light: '#D4A22F',
    dark: '#9A7318',
  },
  active: {
    light: '#C49529',
    dark: '#8A6615',
  },
};

export interface GoldButtonProps {
  /**
   * Enable shimmer animation effect.
   * Default is true for attention-grabbing CTAs.
   */
  shimmer?: boolean;
  /**
   * Shimmer animation duration in seconds.
   * Default is 2.5 seconds (slightly slower for elegance).
   */
  shimmerDuration?: number;
  /**
   * Shimmer opacity (0 to 1).
   * Default is 0.35 for subtle premium effect.
   */
  shimmerOpacity?: number;
}

/**
 * GoldButton - A styled MUI Button with a premium gold gradient and optional shimmer effect.
 *
 * The gold gradient creates a premium, attention-grabbing CTA button.
 * Supports both button and link behavior via MUI's native href prop.
 *
 * @example
 * // Default with shimmer
 * <GoldButton href="/signup">
 *   Start Your Free Trial
 * </GoldButton>
 *
 * @example
 * // Without shimmer
 * <GoldButton shimmer={false} onClick={handleClick}>
 *   Upgrade to Pro
 * </GoldButton>
 *
 * @example
 * // Full width with icon
 * <GoldButton fullWidth size="large" startIcon={<StarIcon />}>
 *   Premium Feature
 * </GoldButton>
 */
const GoldButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'shimmer' && prop !== 'shimmerDuration' && prop !== 'shimmerOpacity',
})<GoldButtonProps>(({ shimmer = true, shimmerDuration = 2.5, shimmerOpacity = 0.35 }) => ({
  // Base styles
  background: `linear-gradient(180deg, ${GOLD_COLORS.light} 0%, ${GOLD_COLORS.dark} 100%)`,
  color: '#FFFFFF',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '100px',
  border: 'none',
  boxShadow: '0 4px 12px rgba(179, 134, 31, 0.3)',
  position: 'relative',
  overflow: 'hidden',

  // Hover state
  '&:hover': {
    background: `linear-gradient(180deg, ${GOLD_COLORS.hover.light} 0%, ${GOLD_COLORS.hover.dark} 100%)`,
    boxShadow: '0 6px 16px rgba(179, 134, 31, 0.4)',
  },

  // Active/pressed state
  '&:active': {
    background: `linear-gradient(180deg, ${GOLD_COLORS.active.light} 0%, ${GOLD_COLORS.active.dark} 100%)`,
    boxShadow: '0 2px 8px rgba(179, 134, 31, 0.3)',
  },

  // Focus visible state for accessibility
  '&:focus-visible': {
    outline: `2px solid ${GOLD_COLORS.light}`,
    outlineOffset: '2px',
  },

  // Disabled state
  '&.Mui-disabled': {
    background: 'linear-gradient(180deg, #D4D4D4 0%, #A3A3A3 100%)',
    color: 'rgba(255, 255, 255, 0.7)',
    boxShadow: 'none',
  },

  // Shimmer effect (conditional)
  ...(shimmer && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: `linear-gradient(90deg, transparent, rgba(255, 255, 255, ${shimmerOpacity}), transparent)`,
      animation: `goldShimmer ${shimmerDuration}s infinite`,
      borderRadius: 'inherit',
    },
    '@keyframes goldShimmer': {
      '0%': { left: '-100%' },
      '100%': { left: '100%' },
    },
  }),

  // Reduced motion support for accessibility
  '@media (prefers-reduced-motion: reduce)': {
    '&::before': {
      animation: 'none',
    },
  },
}));

export default GoldButton;
